class ProductDetails extends HTMLElement {
  constructor() {
    super();
    this.product = window.product;
    this.updateTimer = null;
    this.lastVariantId = null;
    this.lastQuantity = null;
  }

  connectedCallback() {
    this.init();
  }

  init() {
    // Initial update after a short delay
    setTimeout(() => {
      this.updateButtonPrice();
      // Re-apply after page fully loads to handle any late updates
      setTimeout(() => this.updateButtonPrice(), 500);
    }, 100);

    // Set up multiple detection methods
    this.setupMutationObserver();
    this.setupEventListeners();
    this.setupPolling();
    this.interceptVariantChanges();
  }

  setupMutationObserver() {
    this.observer = new MutationObserver((mutations) => {
      let shouldUpdate = false;
      
      for (const mutation of mutations) {
        if (mutation.type === 'attributes' && mutation.attributeName === 'value') {
          const target = mutation.target;
          if (target.name === 'id' || target.name === 'quantity') {
            shouldUpdate = true;
          }
        }
      }
      
      if (shouldUpdate) {
        this.debouncedUpdate();
      }
    });

    // Observe the form for attribute changes
    const form = this.querySelector('.product-form form');
    if (form) {
      this.observer.observe(form, {
        attributes: true,
        attributeFilter: ['value'],
        subtree: true
      });
    }
  }

  setupEventListeners() {
    // Listen to all change events within this element
    this.addEventListener('change', (e) => {
      if (e.target.matches('select, input[type="radio"], input[name="quantity"]')) {
        this.debouncedUpdate();
      }
    }, true);
    
    // Listen to input events for quantity
    this.addEventListener('input', (e) => {
      if (e.target.name === 'quantity') {
        this.debouncedUpdate();
      }
    }, true);
    
    // Listen to click events for quantity buttons
    this.addEventListener('click', (e) => {
      if (e.target.closest('[data-quantity-button], .quantity__button, button[name="plus"], button[name="minus"]')) {
        setTimeout(() => this.debouncedUpdate(), 50);
      }
    }, true);
  }

  setupPolling() {
    // Poll for changes as a fallback
    setInterval(() => {
      const elements = this.getElements();
      const currentVariantId = elements.variantIdInput?.value;
      const currentQuantity = elements.quantityInput?.value || '1';
      
      if (currentVariantId !== this.lastVariantId || currentQuantity !== this.lastQuantity) {
        this.lastVariantId = currentVariantId;
        this.lastQuantity = currentQuantity;
        this.updateButtonPrice();
      }
    }, 250);
  }

  interceptVariantChanges() {
    // Try to intercept the variant ID input changes
    const variantInput = this.querySelector('input[name="id"]');
    if (!variantInput) return;
    
    // Store the original setter
    const descriptor = Object.getOwnPropertyDescriptor(HTMLInputElement.prototype, 'value');
    if (!descriptor) return;
    
    // Create a new property that intercepts value changes
    try {
      Object.defineProperty(variantInput, 'value', {
        get: function() {
          return descriptor.get.call(this);
        },
        set: function(value) {
          const oldValue = this.value;
          descriptor.set.call(this, value);
          
          // Trigger update if value changed
          if (oldValue !== value) {
            const productDetails = this.closest('product-details');
            if (productDetails) {
              productDetails.debouncedUpdate();
            }
          }
        },
        configurable: true
      });
    } catch (e) {
      // If we can't override, that's okay - we have other methods
    }
  }

  debouncedUpdate() {
    clearTimeout(this.updateTimer);
    this.updateTimer = setTimeout(() => {
      this.updateButtonPrice();
    }, 100);
  }

  updateButtonPrice() {
    const elements = this.getElements();
    if (!elements.buttonText || !elements.variantIdInput) return;

    const variantId = elements.variantIdInput.value;
    if (!variantId) return;

    const variant = this.product?.variants?.find(v => v.id == variantId);
    if (!variant) return;

    const quantity = parseInt(elements.quantityInput?.value || 1);
    let unitPrice = variant.price;

    // Check if subscription widget is active and use subscription price
    const sellingPlanId = this.getActiveSellingPlanId();
    if (sellingPlanId) {
      try {
        const spData = document.querySelector('[data-sw-selling-plans]');
        if (spData) {
          const data = JSON.parse(spData.textContent);
          const allocations = data.allocations[variantId];
          if (allocations) {
            const alloc = allocations.find(a => a.selling_plan_id == sellingPlanId) || allocations[0];
            if (alloc) unitPrice = alloc.price;
          }
        }
      } catch (e) { /* use variant price as fallback */ }
    }

    let totalPrice = unitPrice * quantity;

    // Apply automatic discounts based on quantity
    if (quantity >= 5) {
      totalPrice = totalPrice * 0.6; // 40% off for 5+ items
    } else if (quantity >= 3) {
      totalPrice = totalPrice * 0.7; // 30% off for 3+ items
    }

    // Store current button state
    const isDisabled = elements.submitButton?.disabled;

    // Update button content
    if (variant.available) {
      const formattedPrice = this.formatMoney(totalPrice);
      const defaultText = elements.button?.dataset.defaultText || 'Add to Cart';

      // Update the .btn--text element specifically
      elements.buttonText.innerHTML = `
        <span class="btn--text-label">${defaultText}</span>
        <span class="btn--text-price" data-price="${totalPrice}"> - ${formattedPrice}</span>
      `;

      // Make sure button stays enabled if it should be
      if (elements.submitButton && isDisabled === false) {
        elements.submitButton.disabled = false;
      }
    } else {
      elements.buttonText.innerHTML = elements.button?.dataset.soldOutText || 'Sold Out';

      if (elements.submitButton) {
        elements.submitButton.disabled = true;
      }
    }

    // Force the button to maintain our changes
    this.protectButtonContent(elements.buttonText);
  }

  protectButtonContent(buttonText) {
    // Create a mutation observer specifically for the .btn--text element
    if (this.buttonObserver) {
      this.buttonObserver.disconnect();
    }

    this.buttonObserver = new MutationObserver((mutations) => {
      // Check if our price structure is intact
      const hasPriceElement = buttonText.querySelector('.btn--text-price');
      const hasLabelElement = buttonText.querySelector('.btn--text-label');

      // If the structure was removed (e.g., by textContent assignment), restore it
      if (!hasPriceElement || !hasLabelElement) {
        // Use requestAnimationFrame to update on next frame, avoiding race conditions
        requestAnimationFrame(() => {
          this.updateButtonPrice();
        });
      }
    });

    this.buttonObserver.observe(buttonText, {
      childList: true,
      subtree: false  // Only watch direct children since we're on .btn--text now
    });

    // Don't disconnect - keep observing to handle all variant changes
  }

  getActiveSellingPlanId() {
    // Read subscription state from the widget UI rather than the hidden input
    // (which can be cleared by Seal Subscriptions JS)
    const widget = document.querySelector('.subscription-widget');
    if (!widget || widget.style.display === 'none') return null;

    const subscribeRadio = widget.querySelector('[data-sw-option="subscribe"] input[type="radio"]');
    if (!subscribeRadio?.checked) return null;

    const frequencySelect = widget.querySelector('[data-sw-frequency]');
    return frequencySelect?.value || null;
  }

  getElements() {
    return {
      form: this.querySelector('.product-form form'),
      button: this.querySelector('[data-add-to-cart-text]'),
      buttonText: this.querySelector('[data-add-to-cart-text] .btn--text') || this.querySelector('.btn--text'),
      quantityInput: this.querySelector('input[name="quantity"]') || this.querySelector('.quantity-bundle-selector-wrapper input[name="quantity"]'),
      variantIdInput: this.querySelector('input[name="id"]'),
      priceElement: this.querySelector('[data-add-to-cart-text] .btn--text-price'),
      labelElement: this.querySelector('[data-add-to-cart-text] .btn--text-label'),
      submitButton: this.querySelector('button[type="submit"]')
    };
  }

  formatMoney(cents) {
    if (window.Shopify?.formatMoney && window.theme?.moneyFormat) {
      return window.Shopify.formatMoney(cents, window.theme.moneyFormat);
    }
    
    const amount = (cents / 100).toFixed(2);
    const currency = window.Shopify?.currency?.active || 'USD';
    
    const currencySymbols = {
      'USD': '$',
      'EUR': '€',
      'GBP': '£',
      'CAD': '$'
    };
    
    const symbol = currencySymbols[currency] || '';
    return symbol ? `${symbol}${amount}` : `${amount} ${currency}`;
  }

  disconnectedCallback() {
    if (this.observer) {
      this.observer.disconnect();
    }
    if (this.buttonObserver) {
      this.buttonObserver.disconnect();
    }
    clearTimeout(this.updateTimer);
  }
}

// Register the custom element
customElements.define('product-details', ProductDetails);

// Add styles
const style = document.createElement('style');
style.textContent = `
  .btn-addtocart {
    min-width: 250px;
    transition: all 0.2s ease;
  }
  
  .btn-addtocart .btn--text {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.3rem;
    white-space: nowrap;
  }
  
  .btn--text-price {
    font-weight: 600;
    opacity: 0.9;
  }
  
  /* Ensure the price stays visible */
  .btn--text-label,
  .btn--text-price {
    display: inline-flex !important;
  }
  
  @media (max-width: 768px) {
    .btn-addtocart {
      min-width: 100%;
    }
    
    .btn-addtocart .btn--text {
      font-size: 0.95rem;
    }
  }
`;
document.head.appendChild(style);